import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, API_BASE, fetchWithAuth } from "../lib/api";
import { Sparkles, ArrowLeft, Plane, Building2, FileText, Upload } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function ManualPaste() {
  const [content, setContent] = useState("");
  const [subject, setSubject] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const submitText = async (e) => {
    e.preventDefault();
    if (content.trim().length < 10) {
      toast.error("Paste a longer email body");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/manual", {
        content,
        subject: subject || null,
      });
      setResult(data);
      if (data.reservations_saved > 0) {
        toast.success(`Extracted ${data.reservations_saved} reservation(s)`);
      } else {
        toast.warning("No reservations could be extracted from that email");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Extraction failed");
    } finally {
      setBusy(false);
    }
  };

  const submitPdf = async () => {
    if (!pdfFile) { toast.error("Choose a PDF first"); return; }
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", pdfFile);
      if (subject) form.append("subject", subject);
      // Multipart upload with cookie auth + CSRF and one-shot silent refresh
      const res = await fetchWithAuth(`${API_BASE}/manual/pdf`, {
        method: "POST",
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw { response: { data } };
      setResult(data);
      if (data.reservations_saved > 0) {
        toast.success(`Extracted ${data.reservations_saved} reservation(s) from PDF`);
      } else {
        toast.warning("No reservations could be extracted from that PDF");
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "PDF extraction failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <main className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 py-10 md:py-16" data-testid="manual-paste-page">
        <Link to="/home" className="inline-flex items-center text-sm text-sand-600 hover:text-ember mb-8" data-testid="manual-back">
          <ArrowLeft size={14} className="mr-2" /> Dashboard
        </Link>

        <p className="label-tag mb-3">Manual ingest</p>
        <h1 className="heading-h1 text-4xl sm:text-5xl mb-3">Paste or upload.</h1>
        <div className="divider-strong mb-6" />
        <p className="text-sand-600 max-w-xl mb-10 leading-relaxed">
          Paste the body of an itinerary email, or upload a PDF (boarding pass, e-ticket, hotel confirmation).
          Claude Sonnet 4.6 will extract structured reservations and add them to the right trip.
        </p>

        {/* Subject — shared between text and PDF */}
        <div className="mb-6">
          <label className="label-tag block mb-2">Subject (optional)</label>
          <input
            className="swiss-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Booking confirmation – flight DEL → NRT"
            data-testid="manual-subject-input"
          />
        </div>

        {/* PDF upload card */}
        <div className="rounded-card border border-hairline bg-surface p-6 mb-8" data-testid="pdf-upload-card">
          <div className="flex items-center gap-3 mb-4">
            <FileText size={18} className="text-ember" />
            <p className="label-tag">PDF upload</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <label className="flex-1 border border-dashed border-sand-300 px-4 py-6 text-sm text-sand-600 cursor-pointer hover:bg-sand-50 transition-colors text-center">
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="manual-pdf-input"
              />
              {pdfFile ? (
                <span>
                  <FileText size={14} className="inline mr-2 -mt-0.5" />
                  <span className="font-mono text-sand-900">{pdfFile.name}</span>{" "}
                  <span className="text-xs text-sand-500">({(pdfFile.size / 1024).toFixed(0)} KB)</span>
                </span>
              ) : (
                <span><Upload size={14} className="inline mr-2 -mt-0.5" />Click to choose a PDF (max 10 MB)</span>
              )}
            </label>
            <button
              onClick={submitPdf}
              disabled={busy || !pdfFile}
              className="btn-swiss btn-primary disabled:opacity-50"
              data-testid="manual-pdf-submit"
            >
              <Sparkles size={14} className="mr-2" />
              {busy ? "Extracting…" : "Extract from PDF"}
            </button>
          </div>
        </div>

        {/* OR divider */}
        <div className="flex items-center gap-4 my-8">
          <div className="flex-1 h-px bg-sand-200" />
          <span className="label-tag">OR paste text</span>
          <div className="flex-1 h-px bg-sand-200" />
        </div>

        <form onSubmit={submitText} className="space-y-6" data-testid="manual-paste-form">
          <div>
            <label className="label-tag block mb-2">Email body</label>
            <textarea
              className="swiss-input font-mono text-xs leading-relaxed"
              rows={14}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Paste the full email body — text or HTML. We'll handle parsing."
              data-testid="manual-content-input"
            />
            <p className="text-xs text-sand-500 mt-2">{content.length.toLocaleString()} characters</p>
          </div>

          <div className="flex items-center gap-3">
            <button type="submit" disabled={busy} className="btn-swiss btn-primary disabled:opacity-50" data-testid="manual-submit-btn">
              <Sparkles size={14} className="mr-2" />
              {busy ? "Extracting…" : "Extract from text"}
            </button>
            {busy && <span className="label-tag animate-pulse">Calling Claude…</span>}
          </div>
        </form>

        {result && (
          <div className="mt-12 rounded-card border border-hairline bg-surface p-6 md:p-8" data-testid="manual-result">
            <p className="label-tag mb-3">Extraction result</p>
            <h2 className="heading-h2 text-2xl mb-4">
              {result.reservations_saved} reservation{result.reservations_saved !== 1 ? "s" : ""} saved
            </h2>
            {result.reservations_saved > 0 ? (
              <>
                <ul className="space-y-3 mb-6">
                  {result.reservations.map((r) => (
                    <li key={r.id} className="flex items-start gap-3 border-l-2 border-ember pl-4 py-1">
                      {r.type === "flight" ? <Plane size={16} className="mt-1 text-ember" /> : <Building2 size={16} className="mt-1 text-ember" />}
                      <div className="text-sm">
                        {r.type === "flight" && (
                          <>
                            <span className="font-mono">{r.flight?.airline || ""} {r.flight?.flight_number || ""}</span>
                            <span className="ml-2">{r.flight?.departure_airport || r.flight?.departure_city || "?"} → {r.flight?.arrival_airport || r.flight?.arrival_city || "?"}</span>
                          </>
                        )}
                        {r.type === "hotel" && (
                          <>
                            <span className="font-display font-medium">{r.hotel?.hotel_name || "Hotel"}</span>
                            <span className="ml-2 text-sand-600">{r.hotel?.city || ""}</span>
                          </>
                        )}
                        {r.type === "car" && (
                          <>
                            <span className="font-mono">{r.car?.vendor || "Car"}</span>
                            <span className="ml-2">{r.car?.pickup_location || ""} → {r.car?.dropoff_location || ""}</span>
                          </>
                        )}
                        {r.type === "event" && (
                          <>
                            <span className="font-display font-medium">{r.event?.event_name || "Event"}</span>
                            <span className="ml-2 text-sand-600">{r.event?.venue_name || r.event?.city || ""}</span>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                <button
                  className="btn-swiss btn-outline text-sm"
                  onClick={() => navigate("/home")}
                  data-testid="manual-go-dashboard"
                >
                  See in dashboard →
                </button>
              </>
            ) : (
              <p className="text-sand-600 text-sm">
                Claude didn't find a flight, hotel, car, or event reservation in this content.
                If you uploaded a PDF, make sure it's a confirmation document (not a generic flyer).
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
